import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { Button } from '../ui/Button';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';

export const EmailConfirmedPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      try {
        // Get the token from URL parameters
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        
        if (type === 'signup' && token) {
          // Handle email confirmation
          const { error } = await supabase.auth.verifyOtp({
            token_hash: token,
            type: 'signup'
          });
          
          if (error) {
            console.error('Email confirmation error:', error);
            setStatus('error');
            toast.error('Email confirmation failed');
            return;
          }
        }
        
        setStatus('success');
        toast.success('Email confirmed successfully! Welcome to BOTINO.NET');
        
        // Start countdown
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              navigate('/dashboard');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(timer);
        
      } catch (error) {
        console.error('Confirmation process error:', error);
        setStatus('error');
      }
    };

    handleEmailConfirmation();
  }, [searchParams, navigate]);

  const handleManualRedirect = () => {
    if (status === 'success') {
      navigate('/dashboard');
    } else {
      navigate('/signin');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div 
        className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {status === 'loading' && (
          <>
            <div className="flex justify-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900">
                <Loader className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Patvirtinama el. paštas...
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Prašome palaukti, kol patvirtinsime jūsų el. paštą.
              </p>
            </div>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
            </div>
            
            <div>
              <h1 className="text-3xl font-bold mb-4 text-green-600 dark:text-green-400">
                ✔️ El. paštas patvirtintas!
              </h1>
              <p className="text-lg mb-2 text-gray-900 dark:text-white">
                Dabar galite prisijungti prie savo paskyros.
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Būsite automatiškai nukreiptas(-a) į valdymo skydą per {countdown} sekundžių...
              </p>
            </div>

            <div className="space-y-4">
              <Button 
                variant="primary" 
                className="w-full"
                onClick={handleManualRedirect}
              >
                Eiti į valdymo skydą dabar
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/signin')}
              >
                Eiti į prisijungimo puslapį
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 dark:bg-red-900">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
            </div>
            
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Patvirtinimo klaida
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Nepavyko patvirtinti jūsų el. pašto. Nuoroda gali būti pasenusi arba netinkama.
              </p>
            </div>

            <div className="space-y-4">
              <Button 
                variant="primary" 
                className="w-full"
                onClick={() => navigate('/signin')}
              >
                Bandyti prisijungti
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/signup')}
              >
                Registruotis iš naujo
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};